import React, { useState, useEffect } from 'react';
import './StudentDashboard.css';

const StudentDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [courses, setCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);

  useEffect(() => {
    const allCourses = JSON.parse(localStorage.getItem('courses') || '[]');
    const students = JSON.parse(localStorage.getItem('students') || '[]');
    const currentStudent = students.find(s => s.id === user.id);
    
    setAvailableCourses(allCourses);
    setCourses(currentStudent?.courses || []);
  }, [user]);

  const handleCourseRegistration = (course) => {
    const students = JSON.parse(localStorage.getItem('students') || '[]');
    const studentIndex = students.findIndex(s => s.id === user.id);
    
    if (studentIndex !== -1) {
      if (!students[studentIndex].courses) {
        students[studentIndex].courses = [];
      }
      
      if (!students[studentIndex].courses.find(c => c.id === course.id)) {
        students[studentIndex].courses.push({
          ...course,
          attendance: 0,
          totalClasses: 0,
          marks: {
            assignment: 0,
            quiz: 0,
            mid: 0,
            final: 0
          }
        });
        
        localStorage.setItem('students', JSON.stringify(students));
        setCourses(students[studentIndex].courses);
      }
    }
  };

  // Get assessment details for a course
  const getAssessmentDetails = (courseId) => {
    const allAssessments = JSON.parse(localStorage.getItem('assessments') || '[]');
    const courseAssessments = allAssessments.filter(assessment => 
      assessment.courseId === courseId
    );
    
    const marksData = JSON.parse(localStorage.getItem('marks') || '{}');
    
    return courseAssessments.map(assessment => {
      const studentMarks = marksData[`${assessment.id}_${user.id}`] || 0;
      
      return {
        ...assessment,
        studentMarks: studentMarks,
        percentage: assessment.maxMarks > 0 ? Math.round((studentMarks / assessment.maxMarks) * 100) : 0
      };
    });
  };

  // CORRECTED: Calculate total marks using the weighted system from TeacherDashboard
  const calculateTotalMarks = (courseId) => {
    const assessments = getAssessmentDetails(courseId);
    let total = 0;
    
    // Get quiz marks (average of all quizzes) - 15% of total
    const quizzes = assessments.filter(a => a.type === 'quiz');
    if (quizzes.length > 0) {
      const quizTotal = quizzes.reduce((sum, quiz) => sum + quiz.studentMarks, 0);
      const quizAverage = quizTotal / quizzes.length;
      total += (quizAverage / 15) * 15; // 15% of total grade
    }
    
    // Get assignment marks (average of all assignments) - 15% of total
    const assignments = assessments.filter(a => a.type === 'assignment');
    if (assignments.length > 0) {
      const assignmentTotal = assignments.reduce((sum, assignment) => sum + assignment.studentMarks, 0);
      const assignmentAverage = assignmentTotal / assignments.length;
      total += (assignmentAverage / 15) * 15; // 15% of total grade
    }
    
    // Get mid term marks - 30% of total
    const midTerms = assessments.filter(a => a.type === 'mid');
    if (midTerms.length > 0) {
      const midTotal = midTerms.reduce((sum, mid) => sum + mid.studentMarks, 0);
      const midAverage = midTotal / midTerms.length;
      total += (midAverage / 30) * 30; // 30% of total grade
    }
    
    // Get final exam marks - 40% of total
    const finals = assessments.filter(a => a.type === 'final');
    if (finals.length > 0) {
      const finalTotal = finals.reduce((sum, final) => sum + final.studentMarks, 0);
      const finalAverage = finalTotal / finals.length;
      total += (finalAverage / 40) * 40; // 40% of total grade
    }
    
    return Math.round(total);
  };

  // CORRECTED: Get average marks for each category
  const getAverageMarksByType = (courseId, type) => {
    const assessments = getAssessmentDetails(courseId);
    const typeAssessments = assessments.filter(a => a.type === type);
    
    if (typeAssessments.length === 0) return 0;
    
    const total = typeAssessments.reduce((sum, assessment) => sum + assessment.studentMarks, 0);
    return Math.round(total / typeAssessments.length);
  };

  const calculateGrade = (totalMarks) => {
    if (totalMarks >= 90) return 'A+';
    if (totalMarks >= 87) return 'A';
    if (totalMarks >= 84) return 'A-';
    if (totalMarks >= 80) return 'B+';
    if (totalMarks >= 76) return 'B';
    if (totalMarks >= 72) return 'B-';
    if (totalMarks >= 68) return 'C+';
    if (totalMarks >= 64) return 'C';
    if (totalMarks >= 60) return 'C-';
    if (totalMarks >= 50) return 'D';
    return 'F';
  };

  const getAttendanceStatus = (attendance) => {
    if (attendance >= 75) return 'Good';
    if (attendance < 75) return 'DEBAR';
    return 'Bad';
  };

  // Calculate detailed attendance for a course
  const getDetailedAttendance = (courseId) => {
    const classSessions = JSON.parse(localStorage.getItem('classSessions') || '[]')
      .filter(session => session.courseId === courseId);
    
    const attendanceData = JSON.parse(localStorage.getItem('attendance') || '{}');
    
    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;
    
    const sessionDetails = classSessions.map(session => {
      const key = `${session.id}_${user.id}`;
      const status = attendanceData[key] || 'A';
      
      if (status === 'P') presentCount++;
      else if (status === 'A') absentCount++;
      else if (status === 'L') lateCount++;
      
      return {
        date: session.date,
        status: status
      };
    });

    const totalClasses = classSessions.length;
    const attendancePercentage = totalClasses > 0 ? Math.round((presentCount / totalClasses) * 100) : 0;

    return {
      totalClasses,
      presentCount,
      absentCount,
      lateCount,
      attendancePercentage,
      sessionDetails
    };
  };

  // CORRECTED: Get detailed marks for a course using the weighted system
  const getDetailedMarks = (courseId) => {
    const totalMarks = calculateTotalMarks(courseId);
    const grade = calculateGrade(totalMarks);

    // Get average marks for each category
    const quizAvg = getAverageMarksByType(courseId, 'quiz');
    const assignmentAvg = getAverageMarksByType(courseId, 'assignment');
    const midAvg = getAverageMarksByType(courseId, 'mid');
    const finalAvg = getAverageMarksByType(courseId, 'final');

    return {
      quiz: quizAvg,
      assignment: assignmentAvg,
      mid: midAvg,
      final: finalAvg,
      totalMarks,
      grade
    };
  };

  // Calculate overall statistics
  const overallAttendance = courses.length > 0 
    ? Math.round(courses.reduce((acc, course) => {
        const attendance = getDetailedAttendance(course.id).attendancePercentage;
        return acc + attendance;
      }, 0) / courses.length)
    : 0;

  const averageGrade = courses.length > 0 
    ? calculateGrade(courses.reduce((acc, course) => {
        const marks = getDetailedMarks(course.id);
        return acc + marks.totalMarks;
      }, 0) / courses.length)
    : 'N/A';

  return (
    <div className="student-dashboard">
      {/* Header */}
      <header className="student-header">
        <div className="header-left">
          <h1>Student Dashboard</h1>
          <p>Welcome back, {user.name}!</p>
          <p className="student-email">{user.email}</p>
        </div>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </header>

      {/* Navigation */}
      <nav className="student-nav">
        <button 
          className={activeTab === 'dashboard' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button 
          className={activeTab === 'attendance' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('attendance')}
        >
          Attendance
        </button>
        <button 
          className={activeTab === 'results' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('results')}
        >
          Results
        </button>
        <button 
          className={activeTab === 'courses' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('courses')}
        >
          Courses
        </button>
      </nav>

      {/* Content */}
      <main className="student-content">
        {activeTab === 'dashboard' && (
          <div className="tab-content">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Overall Attendance</h3>
                <div className="stat-number">
                  {overallAttendance}%
                </div>
                <p>Across all courses</p>
              </div>
              
              <div className="stat-card">
                <h3>Average Grade</h3>
                <div className="stat-number">
                  {averageGrade}
                </div>
                <p>Current Performance</p>
              </div>
            </div>

            <div className="alerts-section">
              <h3>Alerts</h3>
              {courses.length === 0 ? (
                <p>No courses registered yet</p>
              ) : (
                <div className="alerts">
                  {courses.some(course => {
                    const attendance = getDetailedAttendance(course.id).attendancePercentage;
                    return attendance < 75;
                  }) && (
                    <div className="alert warning">⚠️ Low Attendance in some courses</div>
                  )}
                  {courses.some(course => {
                    const marks = getDetailedMarks(course.id);
                    return marks.grade === 'F';
                  }) && (
                    <div className="alert danger">📉 Poor Grades in some courses</div>
                  )}
                </div>
              )}
            </div>

            <div className="courses-section">
              <h3>My Courses</h3>
              {courses.length === 0 ? (
                <p className="no-data">No courses registered yet. Go to Courses tab to register.</p>
              ) : (
                <table className="courses-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Teacher</th>
                      <th>Attendance</th>
                      <th>Total Marks</th>
                      <th>Grade</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map(course => {
                      const attendance = getDetailedAttendance(course.id);
                      const marks = getDetailedMarks(course.id);
                      
                      return (
                        <tr key={course.id}>
                          <td>{course.code} - {course.name}</td>
                          <td>{course.teacher?.name}</td>
                          <td>{attendance.attendancePercentage}%</td>
                          <td><strong>{marks.totalMarks}/100</strong></td>
                          <td>{marks.grade}</td>
                          <td>
                            <span className={`status ${getAttendanceStatus(attendance.attendancePercentage).toLowerCase()}`}>
                              {getAttendanceStatus(attendance.attendancePercentage)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'attendance' && (
          <div className="tab-content">
            <h2>Attendance</h2>
            {courses.length === 0 ? (
              <p className="no-data">No courses registered yet</p>
            ) : (
              <div className="detailed-attendance">
                {courses.map(course => {
                  const attendance = getDetailedAttendance(course.id);
                  
                  return (
                    <div key={course.id} className="course-attendance-card">
                      <div className="course-attendance-header">
                        <h3>{course.code} - {course.name}</h3>
                        <div className="attendance-summary">
                          <span className="attendance-percentage">{attendance.attendancePercentage}%</span>
                          <span className="attendance-stats">
                            {attendance.presentCount}/{attendance.totalClasses} classes attended
                          </span>
                        </div>
                      </div>
                      
                      {attendance.sessionDetails.length > 0 ? (
                        <div className="session-details">
                          <h4>Class Sessions:</h4>
                          <div className="sessions-list">
                            {attendance.sessionDetails.map((session, index) => (
                              <div key={index} className="session-item">
                                <span className="session-date">
                                  {new Date(session.date).toLocaleDateString()}
                                </span>
                                <span className={`attendance-status ${session.status.toLowerCase()}`}>
                                  {session.status === 'P' ? 'Present' : 
                                   session.status === 'A' ? 'Absent' : 'Late'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="no-sessions">No class sessions recorded yet</p>
                      )}
                      
                      <div className="attendance-breakdown">
                        <div className="breakdown-item">
                          <span className="label">Present:</span>
                          <span className="value present">{attendance.presentCount}</span>
                        </div>
                        <div className="breakdown-item">
                          <span className="label">Absent:</span>
                          <span className="value absent">{attendance.absentCount}</span>
                        </div>
                        <div className="breakdown-item">
                          <span className="label">Late:</span>
                          <span className="value late">{attendance.lateCount}</span>
                        </div>
                        <div className="breakdown-item">
                          <span className="label">Total Classes:</span>
                          <span className="value total">{attendance.totalClasses}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'results' && (
          <div className="tab-content">
            <h2>Results</h2>
            {courses.length === 0 ? (
              <p className="no-data">No courses registered yet</p>
            ) : (
              <div className="detailed-results">
                {courses.map(course => {
                  const assessments = getAssessmentDetails(course.id);
                  const marks = getDetailedMarks(course.id);
                  
                  return (
                    <div key={course.id} className="course-results-card">
                      <div className="course-results-header">
                        <h3>{course.code} - {course.name}</h3>
                        <div className="overall-grade">
                          <span className="grade-label">Overall Grade:</span>
                          <span className={`grade-value grade-${marks.grade.toLowerCase()}`}>
                            {marks.grade}
                          </span>
                          <span className="total-marks-display">({marks.totalMarks}/100)</span>
                        </div>
                      </div>
                      
                      {/* Assessment Details */}
                      <div className="assessment-details">
                        <h4>Assessment Details</h4>
                        {assessments.length === 0 ? (
                          <p className="no-assessments">No assessments recorded yet</p>
                        ) : (
                          <div className="assessments-list">
                            {assessments.map(assessment => (
                              <div key={assessment.id} className="assessment-item">
                                <div className="assessment-info">
                                  <span className={`assessment-type ${assessment.type}`}>
                                    {assessment.type.toUpperCase()}
                                  </span>
                                  <span className="assessment-title">{assessment.title}</span>
                                  <span className="assessment-date">
                                    {new Date(assessment.date).toLocaleDateString()}
                                  </span>
                                </div>
                                <div className="assessment-marks">
                                  <span className="marks-obtained">{assessment.studentMarks}</span>
                                  <span className="marks-separator">/</span>
                                  <span className="marks-total">{assessment.maxMarks}</span>
                                  <span className="marks-percentage">({assessment.percentage}%)</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="marks-breakdown">
                        <h4>Marks Breakdown (Weighted Average)</h4>
                        <div className="marks-grid">
                          <div className="mark-item">
                            <span className="mark-label">Quiz Average:</span>
                            <span className="mark-value">{marks.quiz}/15</span>
                            <div className="weight-info">(15% of total)</div>
                            <div className="mark-bar">
                              <div 
                                className="mark-fill quiz" 
                                style={{width: `${(marks.quiz / 15) * 100}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="mark-item">
                            <span className="mark-label">Assignment Average:</span>
                            <span className="mark-value">{marks.assignment}/15</span>
                            <div className="weight-info">(15% of total)</div>
                            <div className="mark-bar">
                              <div 
                                className="mark-fill assignment" 
                                style={{width: `${(marks.assignment / 15) * 100}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="mark-item">
                            <span className="mark-label">Mid Term Average:</span>
                            <span className="mark-value">{marks.mid}/30</span>
                            <div className="weight-info">(30% of total)</div>
                            <div className="mark-bar">
                              <div 
                                className="mark-fill mid" 
                                style={{width: `${(marks.mid / 30) * 100}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="mark-item">
                            <span className="mark-label">Final Average:</span>
                            <span className="mark-value">{marks.final}/40</span>
                            <div className="weight-info">(40% of total)</div>
                            <div className="mark-bar">
                              <div 
                                className="mark-fill final" 
                                style={{width: `${(marks.final / 40) * 100}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="mark-item total">
                            <span className="mark-label">Total Course Marks:</span>
                            <span className="mark-value total">{marks.totalMarks}/100</span>
                            <div className="mark-bar">
                              <div 
                                className="mark-fill total" 
                                style={{width: `${marks.totalMarks}%`}}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grade-explanation">
                        <h4>Grade Scale:</h4>
                        <div className="grade-scale">
                          <span>A+: 90-100</span>
                          <span>A: 87-89</span>
                          <span>A-: 84-86</span>
                          <span>B+: 80-83</span>
                          <span>B: 76-79</span>
                          <span>B-: 72-75</span>
                          <span>C+: 68-71</span>
                          <span>C: 64-67</span>
                          <span>C-: 60-63</span>
                          <span>D: 50-59</span>
                          <span>F: Below 50</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="tab-content">
            <h2>Available Courses</h2>
            {availableCourses.length === 0 ? (
              <p className="no-data">No courses available. Please ask admin to add courses.</p>
            ) : (
              <div className="courses-grid">
                {availableCourses.map(course => (
                  <div key={course.id} className="course-card">
                    <h3>{course.code} - {course.name}</h3>
                    <p><strong>Instructor:</strong> {course.teacher?.name}</p>
                    <p><strong>Credit Hours:</strong> {course.creditHours}</p>
                    <p><strong>Email:</strong> {course.teacher?.email}</p>
                    <button 
                      onClick={() => handleCourseRegistration(course)}
                      disabled={courses.some(c => c.id === course.id)}
                      className="register-btn"
                    >
                      {courses.some(c => c.id === course.id) ? 'Registered' : 'Register'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentDashboard;